import React, { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'

const decodeToken = (token: string) => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    )

    return JSON.parse(jsonPayload)
  } catch (error) {
    return null
  }
}

export default function Home() {
  const [token, setToken] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const gif1Ref = useRef<HTMLAnchorElement>(null)
  const gif2Ref = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const token = localStorage.getItem('jwtToken')

    if (token) {
      // Token is present in local storage
      // Extract the "nick" parameter from the token
      const decodedToken = decodeToken(token)
      const nick = decodedToken?.nick
      const id = decodedToken?.id

      console.log({ id, nick, decodedToken })

      if (id) {
        window.history.pushState(null, '', `/in/${id}`)
      }
      if (nick) {
        window.history.pushState(null, '', `/in/${nick}`)
      }
    } else {
      // Token is not present in local storage
      // Perform any necessary checks or actions
      console.log('JWT token not found')
    }
  }, [])

  useEffect(() => {
    // Entry animation for GIFs
    if (gif1Ref.current && gif2Ref.current) {
      gsap.fromTo(
        [gif1Ref.current, gif2Ref.current],
        { opacity: 0, scale: 0.95, y: 50 },
        {
          opacity: 1,
          scale: 1,
          y: 0,
          duration: 15,
          ease: 'elastic.out(1, 0.5)',
          stagger: 0.3,
        }
      )
    }

    // Looping animation (gentle pulsing)
    gsap.to([gif1Ref.current, gif2Ref.current], {
      scale: 1,
      duration: 5,
      yoyo: true,
      repeat: -1,
      ease: 'power1.inOut',
    })
  }, [])

  return (
    <div className="flex justify-center items-center h-full">
      <div
        className="bg-white p-4 shadow-md
        sm:w-[calc(100%-20px)]
        sm:max-w-[1024px]
        min-h-[640px]
        mx-auto h-full
        notes-content"
        ref={containerRef}
      >
        <div className="mt-4 mb-4 text-2xl">
          Grow{' '}
          <span className="whitespace-nowrap">
            <span style={{ marginRight: 8 }}>🍏</span>apples
          </span>{' '}
          and craft{' '}
          <span className="whitespace-nowrap">
            <span style={{ marginRight: 8 }}>🧩</span>
            solutions with
          </span>{' '}
          <a href="https://github.com/reactima" target="_blank" rel="noreferrer">
            @reactima
          </a>
        </div>
        <div>
          <a href="/notes">📚 Notes</a> | <a href="/trending">🚀 Github Trending </a>
        </div>

        <div className="flex flex-col items-center justify-center mx-auto p-1 space-y-4  mb-2">
          <a ref={gif1Ref} href="/notes" className="w-full" aria-label="Ramen GIF">
            <img src="/assets/ramen.gif" className="mx-auto w-[100%] mb-1" alt="Ramen GIF" />
            <div className="text-gray-500 text-center">
              Balance builds strength: family and work, sales and engineering—both matter equally, do both{' '}
              <a href="https://github.com/reactima" target="_blank" rel="noreferrer">
                @reactima
              </a>
            </div>
          </a>
        </div>

        <h3>
          Quotes for Dead Founders <s>Poets</s> Society{' '}
          <a href="https://www.youtube.com/watch?v=ye4KFyWu2do" target="_blank" rel="noreferrer" className="text-sm">
            1989
          </a>
        </h3>
        <div
          className="flex bg-green-500 rounded-lg shadow-md"
          style={{
            backgroundImage: 'url(/assets/bg-apple.jpg)',
            filter: 'hue-rotate(276deg)',
            backgroundRepeat: 'repeat',
            backgroundPositionY: '-80px',
            textShadow: '0 0 5px white, 0 0 10px white, 0 0 15px white, 0 0 20px white',
          }}
        >
          {/* First Column */}
          <div className="flex-grow text-sm text-gray-700 space-y-4 pt-0 pb-2 pl-4 pr-4">
            <div className="w-[140px] md:w-[200px] text-sm md:text-lg rounded absolute top-0 right-0 w-1/4 m-1p-2">
              <img
                alt="jobs"
                src="/assets/jobs-card.webp"
                className="w-[140px] md:w-[200px] m-0 rounded-lg shadow-md"
              />
            </div>
            <p>“How many of you are from manufacturing companies?”</p>
            <p className="text-gray-500">[Some hands go up.]</p>
            <p>
              “Oh, excellent. Where are the rest of you from? Okay, <strong>so how many are from consulting?</strong>”
            </p>
            <p className="text-gray-500">[A number of hands go up.]</p>
            <p>
              “<strong>Oh, that’s bad. Yeah, the mind is too important to waste</strong>—you should do something.”
            </p>
            <p>
              “I think that <strong>without owning something over an extended period of time</strong>—like a few
              years—where one has a chance to take responsibility for one’s recommendations, where one has to see those
              recommendations through all action stages, accumulate scar tissue for the mistakes, pick oneself up off
              the ground, and dust oneself off, <strong>one learns only a fraction of what one can.</strong>”
            </p>
            <div>
              <a
                href="https://www.youtube.com/watch?v=Gk-9Fd2mEnI&t=184s"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-700 hover:underline"
              >
                [Watch on YouTube]
              </a>
            </div>
          </div>

          {/* Second Column */}
          <div className="flex-none flex items-end justify-end w-[90px] md:w-[180px]">
            <a href="https://www.youtube.com/watch?v=Gk-9Fd2mEnI&t=184s" target="_blank" rel="noopener noreferrer">
              <img alt="jobs" src="/assets/jobs.png" className="w-[90px] md:w-[180px] m-0 rounded-lg" />
            </a>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center mx-auto p-1 space-y-4" suppressHydrationWarning>
          <a ref={gif2Ref} href="/notes" className="w-full" aria-label="Fight GIF">
            <img src="/assets/fight.gif" className="mx-auto w-[100%]" alt="Fight GIF" />
            <div className="text-gray-500 text-center">
              Bad people will never watch the whole Naturo{' '}
              <a href="https://github.com/reactima" target="_blank" rel="noreferrer">
                @reactima
              </a>
            </div>
          </a>
        </div>
      </div>
    </div>
  )
}
